import { useRef, useState } from "react";
import { FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { COUNTRY_BY_CODE, salaryToInput } from "@/lib/countries";

// Map the /jobs/parse-jd result onto the job form state (only fields the JD mentioned).
export function applyParsedJd(prev, d) {
  const next = { ...prev, description: d.description };
  if (d.title) next.title = d.title;
  if (d.country_code && COUNTRY_BY_CODE[d.country_code]) {
    next.country_code = d.country_code;
    next.salary_currency = COUNTRY_BY_CODE[d.country_code].currency;
  }
  if (d.city) next.city = d.city;
  if (d.remote !== null && d.remote !== undefined) next.remote = d.remote;
  if (d.job_type) next.job_type = d.job_type;
  if (d.max_notice_days) next.max_notice_days = String(d.max_notice_days);
  if (d.salary_min || d.salary_max) {
    const cur = d.salary_currency || (d.country_code && COUNTRY_BY_CODE[d.country_code]?.currency) || prev.salary_currency;
    const unit = cur === "INR" ? (d.salary_unit || "lpa") : prev.salary_unit;
    Object.assign(next, { salary_currency: cur, salary_unit: unit,
      salary_min: salaryToInput(d.salary_min, unit), salary_max: salaryToInput(d.salary_max, unit) });
  }
  return next;
}

export default function JdUpload({ onParsed, hasDescription = false, confirmReplace = false, prefix = "job" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (confirmReplace && hasDescription && !window.confirm("Replace the current job description with the uploaded JD?")) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/jobs/parse-jd", fd);
      onParsed(data);
      toast.success(`Filled from ${data.filename} — please review before saving`);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Couldn't read that file");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div
      data-testid={`${prefix}-jd-upload`}
      onClick={() => !busy && ref.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border border-dashed cursor-pointer transition-colors ${drag ? "border-indigo-500 bg-indigo-50" : "border-[color:var(--bd-sub)] hover:border-indigo-400 hover:bg-indigo-50/40"}`}
    >
      {busy ? <Loader2 className="w-5 h-5 text-indigo-600 animate-spin shrink-0" /> : <FileUp className="w-5 h-5 text-indigo-600 shrink-0" />}
      <div className="min-w-0 text-sm">
        <div className="font-medium" data-testid={`${prefix}-jd-upload-label`}>{busy ? "Reading your JD…" : "Upload JD (PDF or Word)"}</div>
        <div className="text-xs text-slate-500">{busy ? "Filling title, description, location, salary and skills" : "Drop a file here or click to choose. Fills the form for you to review."}</div>
      </div>
      <input ref={ref} type="file" accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden" data-testid={`${prefix}-jd-file`} onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}
